import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';

type PurgeState = 'pending' | 'failed' | 'purged' | 'restored';

interface DeletionRequest {
  id: number;
  user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  reason_category: string;
  reason_text: string | null;
  requested_via: string;
  acting_admin_id: number | null;
  purge_state: PurgeState;
  purge_at: string | null;
  purge_attempts: number | null;
  last_error: string | null;
  reminder_sent_at: string | null;
  restored_at: string | null;
  purged_at: string | null;
  swept_amount: string | null;
  created_at: string | null;
}

interface ListResponse {
  data: DeletionRequest[];
  meta: { page: number; per_page: number; total: number; state: string };
}

const TABS: { value: PurgeState | 'all'; label: string; hint: string }[] = [
  {
    value: 'failed',
    label: 'Needs attention',
    hint: 'Purges that stalled. A stuck balance here means money sitting in a locked wallet.',
  },
  {
    value: 'pending',
    label: 'In grace period',
    hint: 'Locked and restorable until the purge date.',
  },
  {
    value: 'purged',
    label: 'Purged',
    hint: 'Anonymised; the record lives in the archive.',
  },
  {
    value: 'restored',
    label: 'Restored',
    hint: 'The user changed their mind in time.',
  },
];

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function daysUntil(iso: string | null) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
}

function StateBadge({ state }: { state: PurgeState }) {
  const tone =
    state === 'failed'
      ? 'bg-red-100 text-red-700'
      : state === 'pending'
        ? 'bg-amber-100 text-amber-700'
        : state === 'restored'
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${tone}`}
    >
      {state}
    </span>
  );
}

export function AccountDeletionsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('account_deletion.manage');

  const [state, setState] = useState<PurgeState | 'all'>('failed');
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ListResponse>(
        '/back-office/account-deletions',
        {
          params: { state, per_page: 50 },
        }
      );
      setData(res.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load deletion requests'));
    } finally {
      setLoading(false);
    }
  }, [state]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (row: DeletionRequest) => {
    const note = window.prompt(
      `Restore ${row.email ?? `user ${row.user_id}`}? Give a reason for the audit trail:`
    );
    if (note === null) return;
    setActing(row.user_id);
    try {
      await api.post(`/back-office/users/${row.user_id}/restore-account`, {
        note: note || undefined,
      });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Restore failed'));
    } finally {
      setActing(null);
    }
  };

  const rows = data?.data ?? [];
  const activeTab = TABS.find((t) => t.value === state);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">
          Account deletions
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Self-service deletion requests. Accounts in the grace period are
          locked and unlisted but fully restorable.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setState(tab.value)}
            className={`rounded px-3 py-1.5 text-sm font-medium ${
              state === tab.value
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab && <p className="text-xs text-slate-500">{activeTab.hint}</p>}

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Requested</th>
              <th className="px-3 py-2">Purge date</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Problem</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  Loading…
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  {state === 'failed'
                    ? 'Nothing needs attention. Every purge has completed cleanly.'
                    : 'No requests in this state.'}
                </td>
              </tr>
            )}

            {rows.map((row) => {
              const left = daysUntil(row.purge_at);
              return (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <Link
                      to={`/users/${row.user_id}`}
                      className="font-medium text-slate-900 hover:underline"
                    >
                      {row.first_name || row.last_name
                        ? `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim()
                        : `User ${row.user_id}`}
                    </Link>
                    <div className="text-xs text-slate-500">
                      {row.email ?? '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-slate-700">
                      {row.reason_category.replace(/_/g, ' ')}
                    </div>
                    {row.reason_text && (
                      <div
                        className="max-w-xs truncate text-xs text-slate-500"
                        title={row.reason_text}
                      >
                        “{row.reason_text}”
                      </div>
                    )}
                    {row.requested_via === 'back_office' && (
                      <div className="text-xs text-amber-700">
                        via back office
                        {row.acting_admin_id
                          ? ` (admin ${row.acting_admin_id})`
                          : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {formatDate(row.created_at)}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {formatDate(row.purge_at)}
                    {row.purge_state === 'pending' && left !== null && (
                      <div className="text-xs text-slate-500">
                        {left > 0 ? `${left} day(s) left` : 'due now'}
                        {row.reminder_sent_at ? ' · reminded' : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StateBadge state={row.purge_state} />
                    {row.swept_amount && (
                      <div className="text-xs text-slate-500">
                        swept ₦{row.swept_amount}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {row.last_error ? (
                      <div className="max-w-xs text-xs text-red-700">
                        {row.last_error}
                        {(row.purge_attempts ?? 0) > 0 && (
                          <span className="text-slate-400">
                            {' '}
                            ({row.purge_attempts} attempt
                            {row.purge_attempts === 1 ? '' : 's'})
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/* Only a pending request can be restored — a purged
                        account has been anonymised and there is nothing to
                        bring back. */}
                    {canManage && row.purge_state === 'pending' && (
                      <button
                        type="button"
                        onClick={() => restore(row)}
                        disabled={acting === row.user_id}
                        className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                      >
                        {acting === row.user_id ? 'Restoring…' : 'Restore'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data && (
        <p className="text-xs text-slate-500">
          {data.meta.total} request(s) in this state.
        </p>
      )}
    </div>
  );
}
